import React, { useEffect, useMemo, useState } from "react";
import { CompetitionsResponse, SubscriptionResponse } from "../management-subscription/types";
import { SubscriptionCreatedContext } from "./subscriptionContext";
import { getAvailableSubscriptions, getBotbPassCompetitions } from "../botb-pass/subscription-service";
import { reactivateSubscription } from "./subscription-service";
import { useGetSubscriptionPlanQuery } from "@/app/redux/features/api/common";
import dayjs from "dayjs";
import { SubscriptionCustomFeature, SubscriptionFeatures } from "./interfaces";
import { getCookie } from "../../../helpers/cookies";

export const SubscriptionContext = ({
  children,
  cancelRequests = false,
}: {
  children: React.ReactNode;
  cancelRequests?: boolean;
}) => {
  const [allPackages, setAllPackages] = useState<Package[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionPlanUser>({
    amount: 0,
    cancel_at_period_end: false,
    currency: "",
    current_period_end: 0,
    current_period_start: 0,
    description: "",
    features: [],
    id: "",
    interval: "",
    intervalCount: 0,
    metadata: [],
    name: "",
    prices: [],
    productId: "",
    status: "",
  });
  const subsWillBeCancelled = useMemo(() => subscription.cancel_at_period_end === true, [subscription]);
  const subscriptionStatus = subsWillBeCancelled ? "Cancelled" : "Active";
  const cancellationSubscriptionText = subsWillBeCancelled ? (
    <>
      Your subscription will be cancelled on{" "}
      <span className={"text-secondary"}>{dayjs(subscription.current_period_end * 1000).format("DD MMMM YYYY")}</span>
    </>
  ) : null;
  const [botbCompetitions, setBotbCompetitions] = useState<CompetitionsResponse>({
    lost: [],
    played: [],
  });
  const [isCompetitionsLoading, setCompetitionsLoading] = useState<boolean>(true);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState<boolean>(true);
  const activePricePlan = useMemo<SubscriptionPriceUser>(
    () =>
      subscription.prices.find(price => price.active) || {
        active: false,
        billing_scheme: "",
        created: 0,
        currency: "",
        custom_unit_amount: 0,
        id: "",
        livemode: false,
        lookup_key: "",
        metadata: { base_price: "", discount_value_card: "", upsell_discount_value_switch: "" },
        nickname: "",
        object: "",
        product: undefined,
        recurring: { interval: "", interval_count: 0, meter: undefined, trial_period_days: undefined, usage_type: "" },
        tax_behavior: "",
        tiers_mode: "",
        transform_quantity: undefined,
        type: "",
        unit_amount: 0,
        unit_amount_decimal: "",
      },
    [subscription]
  );
  const [isBenefitsModalOpen, setIsBenefitsModalOpen] = useState<boolean>(false);
  const [customSubscriptionFeatures, setCustomSubscriptionFeatures] = useState<SubscriptionCustomFeature[]>([]);
  const [isRenewSubscriptionModalOpen, setIsRenewSubscriptionModalOpen] = useState<boolean>(false);
  const [isRenewButtonDisabled, setIsRenewButtonDisabled] = useState<boolean>(false);
  const [isInvoiceProcessing, setIsInvoiceProcessing] = useState<boolean>(false);

  const isAuth = getCookie("BotB.Authenticated") === "true";
  const {
    data: subscriptionPlanQueryData,
    isLoading: isSubscriptionQueryLoading,
    isError: isSubscriptionQueryError,
  } = useGetSubscriptionPlanQuery(undefined, { skip: cancelRequests || !isAuth });

  const tryAndGetValue = (key: string, metaData: [string, string][]) => {
    const item = metaData.find(item => item[0] === key);
    if (item) {
      return item[1];
    }
    return "0";
  };

  const getFeatureEntries = (feature: string): number => {
    const reg = /([0-9]+)\s(tickets|entries)/;
    const match = feature.match(reg);
    if (match?.[1]) {
      return Number(match[1]);
    }
    return -1;
  };

  const formatDescription = (description: string): React.JSX.Element => {
    const totalPrice = /£.*M/.exec(description);
    if (!totalPrice?.length) {
      return <></>;
    }
    const parsedPrice = totalPrice[0];
    return (
      <>
        Your membership gives you{" "}
        <span className="font-medium">{totalEntries} entries across 21 monthly competitions,</span> with prizes worth
        over <span className="font-medium">{parsedPrice}.</span> <br />
        From dream homes and supercars to life changing cash, here’s what you can win:
      </>
    );
  };

  useEffect(() => {
    if (cancelRequests) return;
    if (!isAuth) {
      location.href = `/login?ReturnUrl=${encodeURIComponent(location.pathname + location.search)}`;
      return;
    }
    getAvailableSubscriptions()
      .then(res => res.json())
      .then((res: Package[]) => {
        if (Array.isArray(res)) {
          setAllPackages(res);
        }
      })
      .catch(err => console.error(err));
    getBotbPassCompetitions()
      .then(res => res.json())
      .then((res: CompetitionsResponse) => {
        setBotbCompetitions(res);
        setCompetitionsLoading(false);
      })
      .catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (isSubscriptionQueryLoading) return;
    if (isSubscriptionQueryError || !subscriptionPlanQueryData) {
      if (!cancelRequests && isAuth) {
        location.href = "/botb-pass";
      }
      setIsSubscriptionLoading(false);
      return;
    }
    const subscriptionData = subscriptionPlanQueryData;
    setSubscription(subscriptionData);
    const houseFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.DreamHouse,
      entries: 0,
      image: "/assets/images/subscriptions/house.webp",
      multiplier: 1,
      text: "",
    };
    const supercarFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.LuxuryCars,
      image: "/assets/images/subscriptions/xc-supercars.webp",
      entries: 0,
      multiplier: 6,
      text: "",
    };
    const lifestyleFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.LifestylePrizes,
      image: "/assets/images/subscriptions/lifestyle.webp",
      entries: 0,
      multiplier: 4,
      text: "",
    };
    const dreamCarFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.DreamCars,
      image: "/assets/images/subscriptions/stb-cars.webp",
      entries: 0,
      multiplier: 4,
      text: "",
    };
    const instantWinFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.InstantWins,
      image: "/assets/images/subscriptions/any-instant-wins.webp",
      entries: 0,
      multiplier: -1,
      text: "",
    };

    let hcTickets = "0";
    let xcTickets = "0";
    let lsTickets = "0";
    let dcTickets = "0";
    let iwTickets = "0";
    let scTickets = "0";

    if (subscriptionData.metadata !== null) {
      hcTickets = tryAndGetValue("HC", subscriptionData.metadata);
      xcTickets = tryAndGetValue("XC", subscriptionData.metadata);
      lsTickets = tryAndGetValue("LS", subscriptionData.metadata);
      dcTickets = tryAndGetValue("DC", subscriptionData.metadata);
      iwTickets = tryAndGetValue("IW", subscriptionData.metadata);
      scTickets = tryAndGetValue("SC", subscriptionData.metadata);
    }

    if (subscriptionData.features !== null) {
      for (const feature of subscriptionData.features) {
        if (feature.includes("Dream Home")) {
          houseFeature.text = (
            <>
              With the Pass, you’ll have{" "}
              <strong>
                {hcTickets} {hcTickets == "1" ? "entry" : "entries"}
              </strong>{" "}
              in the monthly Dream Home competition, giving you the chance to win the prize of a lifetime.
            </>
          );
          houseFeature.entries = getFeatureEntries(feature);
        } else if (feature.includes("Luxury Cars") || feature.includes("Supercards")) {
          supercarFeature.text = (
            <>
              Get behind the wheel of a luxury or sports car! You’ll receive{" "}
              <strong>
                {xcTickets} {xcTickets == "1" ? "guaranteed ticket" : "guaranteed tickets"}
              </strong>{" "}
              in any Luxury Car competition each month. With at least 6 distinct competitions monthly, your chances to
              win are stronger than ever.
            </>
          );
          supercarFeature.entries = getFeatureEntries(feature);
        } else if (feature.includes("Lifestyle")) {
          lifestyleFeature.text = (
            <>
              This is the competition where you choose what you want to win: cars, cash, tech, experiences, and more.
              With your subscription, you get{" "}
              <strong>
                {lsTickets} {lsTickets == "1" ? "ticket" : "tickets"}
              </strong>{" "}
              for each of the 4 Lifestyle competitions in a month.
            </>
          );
          lifestyleFeature.entries = getFeatureEntries(feature);
        } else if (feature.includes("Dream Car")) {
          dreamCarFeature.text = (
            <>
              Enjoy{" "}
              <strong>
                {dcTickets} {dcTickets == "1" ? "weekly ticket" : "weekly tickets"}
              </strong>{" "}
              for the iconic Spot The Ball game of skill. Compete every week and choose your prize from an extensive
              selection of brand-new cars!
            </>
          );
          dreamCarFeature.entries = getFeatureEntries(feature);
        } else if (feature.includes("Instant Win")) {
          instantWinFeature.text = (
            <>
              With{" "}
              <strong>
                {iwTickets} {iwTickets == "1" ? "ticket" : "tickets"} included
              </strong>
              , play in any of our Instant Wins competitions each month! Instant Wins is all about luck, so choose
              carefully where to use your entries.
            </>
          );
          instantWinFeature.entries = getFeatureEntries(feature);
        }
      }
    }

    const passExclusiveFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.PassExclusiveComps,
      image: "/assets/images/subscriptions/pass-exclusive-comps.webp",
      entries: Number(scTickets) || 0,
      multiplier: 30,
      text: (
        <>
          A thank you to <strong>BOTB Pass members</strong>: automatic entry into exclusive daily competitions, with
          1,250 extra prizes every day &mdash; including cash, credit and tech.
        </>
      ),
    };

    setCustomSubscriptionFeatures([
      houseFeature,
      supercarFeature,
      dreamCarFeature,
      lifestyleFeature,
      instantWinFeature,
      passExclusiveFeature,
    ]);
    setIsSubscriptionLoading(false);
  }, [subscriptionPlanQueryData, isSubscriptionQueryLoading, isSubscriptionQueryError]);

  const onRenewSubscription = () => {
    setIsRenewButtonDisabled(true);
    reactivateSubscription()
      .then(res => res.json())
      .then((res: SubscriptionResponse) => {
        setSubscription(prevSubscriptionPlan => ({
          ...prevSubscriptionPlan,
          cancel_at_period_end: res.cancel_at_period_end,
        }));
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setIsRenewButtonDisabled(false);
        setIsRenewSubscriptionModalOpen(false);
      });
  };

  const totalEntries = tryAndGetValue("total_entries", subscription.metadata);
  const description = formatDescription(subscription.description);

  return (
    <SubscriptionCreatedContext.Provider
      value={{
        subscription,
        activePricePlan,
        botbCompetitions,
        isCompetitionsLoading,
        setCompetitionsLoading,
        subsWillBeCancelled,
        setBotbCompetitions,
        allPackages,
        isSubscriptionLoading,
        cancellationSubscriptionText,
        subscriptionStatus,
        setIsBenefitsModalOpen,
        isBenefitsModalOpen,
        customSubscriptionFeatures,
        setIsRenewSubscriptionModalOpen,
        isRenewSubscriptionModalOpen,
        onRenewSubscription,
        isRenewButtonDisabled,
        totalEntries,
        description,
        setIsInvoiceProcessing,
        isInvoiceProcessing,
      }}>
      {children}
    </SubscriptionCreatedContext.Provider>
  );
};
